/**
 * The routes, and the gate in front of all but one of them.
 *
 * ── The gate is `GET /api/relay/me`, and nothing else ───────────────────────
 * A browser on relay.mauvely.com cannot ask the accounts service on
 * mauvely.com with its cookie, so relay-server answers for it: the row it would
 * hand the directory, or 401. 401 means sign in, and the session cookie is
 * `HttpOnly`, so reading it here is not an option and would conclude "signed
 * out" for everybody if it were.
 *
 * ── `/m/:token` is outside the gate, deliberately ───────────────────────────
 * A guest has no account. The link is the whole credential, and a sign-in page
 * in front of it would mean nobody without a Mauvely account could ever join a
 * meeting — which is the entire feature. The route is checked before the gate
 * so the order cannot be got wrong by adding something above it.
 */

import React, { useEffect, useState } from 'react';
import { ds } from './ds';
import { matchRoute, useRouter } from './router';
import { useT } from './i18n';
import { fetchMe, signInUrl } from './lib/api';
import { sampleMode } from './lib/relay/sample-mode';
import { store } from './lib/relay/store';
import { CallProvider } from './state/calls';
import { Empty, Shell } from './components/Shell';
import { ChatsList } from './pages/Chats';
import { ConversationPage } from './pages/Conversation';
import { MeetingsList } from './pages/Meetings';
import { MeetingRoomPage } from './pages/MeetingRoom';
import { TranscriptPage } from './pages/Transcript';
import { BoardsSurface } from './pages/Boards';
import { GuestPage } from './pages/Guest';
import { SettingsPage } from './pages/Settings';
import type { Person } from './lib/relay/types';

type Gate = { state: 'checking' } | { state: 'in'; me: Person } | { state: 'out' } | { state: 'error' };

function SignInGate({ children }: { children: React.ReactNode }) {
  const { Button, Wordmark } = ds();
  const t = useT();
  const [gate, setGate] = useState<Gate>({ state: 'checking' });

  useEffect(() => {
    let cancelled = false;
    // `?sample=1`, development only: the store is the sample one and there is
    // no server to ask who we are. Stepped over rather than stubbed, because a
    // fake answer here is a fake session everywhere below it.
    if (sampleMode()) {
      const me = store.me();
      if (me) setGate({ state: 'in', me });
      return () => {
        cancelled = true;
      };
    }
    fetchMe()
      .then((me) => {
        if (cancelled) return;
        // The store asks the same route on `boot()`, so this is a round trip
        // saved rather than the only way it learns — but it is also what makes
        // the first paint of a name the signed-in one instead of nothing.
        if (me) store.adopt(me);
        setGate(me ? { state: 'in', me } : { state: 'out' });
      })
      .catch(() => {
        if (!cancelled) setGate({ state: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (gate.state === 'checking') {
    return <Centred>{t('app.loading')}</Centred>;
  }

  if (gate.state === 'in') return <>{children}</>;

  const out = gate.state === 'out';
  return (
    <Centred>
      <Wordmark size={24} />
      <h1
        style={{
          fontFamily: 'var(--font-display), sans-serif',
          fontWeight: 800,
          fontSize: 28,
          lineHeight: 1.1,
          margin: 'var(--space-16) 0 var(--space-20)'
        }}
      >
        {out ? t('app.signin.title') : t('app.unreachable.title')}
      </h1>
      {out ? (
        <Button href={signInUrl()}>{t('app.signin.action')}</Button>
      ) : (
        <Button onClick={() => window.location.reload()}>{t('app.unreachable.action')}</Button>
      )}
    </Centred>
  );
}

function Centred({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="mv-app"
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        padding: 'var(--space-24)',
        textAlign: 'center'
      }}
    >
      <div>{children}</div>
    </div>
  );
}

export function App() {
  const { path } = useRouter();

  // Before the gate. See this file's header.
  const guest = matchRoute('/m/:token', path);
  if (guest) return <GuestPage token={guest.token!} />;

  return (
    <SignInGate>
      {/* Above the router, because a ring is not a property of the page that
          happens to be open: a call arriving while somebody is on a board has
          to ring, and the overlay it draws covers whatever is underneath. */}
      <CallProvider>{routed(path)}</CallProvider>
    </SignInGate>
  );
}

function routed(path: string) {
  if (path === '/') {
    return <Shell surface="chats" list={<ChatsList />} pane={<ChatsPlaceholder />} />;
  }

  const conversation = matchRoute('/c/:id', path);
  if (conversation) {
    return (
      <Shell
        surface="chats"
        list={<ChatsList />}
        pane={<ConversationPage id={conversation.id!} />}
      />
    );
  }

  // No list column: the app's Meetings is one full-width surface, and a row
  // that carries a title, a time, a state and four buttons has no business in
  // a 286px column — it overflowed one.
  if (path === '/meetings') {
    return <Shell surface="meetings" pane={<MeetingsList />} />;
  }

  // Before `/meetings/:id`, or the room would match the transcript's path with
  // `id` reading "transcript".
  const transcript = matchRoute('/meetings/:id/transcript', path);
  if (transcript) {
    return <Shell surface="meetings" pane={<TranscriptPage id={transcript.id!} />} />;
  }

  const meeting = matchRoute('/meetings/:id', path);
  if (meeting) {
    return <Shell surface="meetings" pane={<MeetingRoomPage id={meeting.id!} />} />;
  }

  // One pane, with the list inside it beside the plane: the app's shape, and
  // the only one that gives a canvas the whole width.
  if (path === '/boards') {
    return <Shell surface="boards" pane={<BoardsSurface />} />;
  }

  const board = matchRoute('/boards/:id', path);
  if (board) {
    return <Shell surface="boards" pane={<BoardsSurface id={board.id!} />} />;
  }

  if (path === '/settings') {
    return <Shell surface="settings" pane={<SettingsPage />} />;
  }

  return <Shell surface="chats" list={<ChatsList />} pane={<NotFound />} />;
}

function ChatsPlaceholder() {
  const t = useT();
  return <Empty title={t('chats.pick')} />;
}

function NotFound() {
  const t = useT();
  return <Empty title={t('app.notfound.title')} />;
}
